/** Mã giảm giá / khuyến mãi (#18619). Pro-only — the backend 402s on Lite. */
import { apiClient } from "./client";

export type DiscountType = "PERCENT" | "AMOUNT";

/**
 * How this campaign defines "khách hàng mới". The clinic picks per campaign rather than the code
 * fixing one meaning (#18619) — a launch offer usually means "never been treated here", a win-back
 * usually means "registered recently". `null` = the campaign has no new-patient condition.
 */
export type NewPatientRule = "FIRST_ENCOUNTER" | "FIRST_INVOICE" | "REGISTERED_WITHIN_DAYS";

export const NEW_PATIENT_RULE_LABELS: Record<NewPatientRule, string> = {
  FIRST_ENCOUNTER: "Chưa từng khám tại phòng khám",
  FIRST_INVOICE: "Chưa từng có hoá đơn",
  REGISTERED_WITHIN_DAYS: "Đăng ký trong vòng N ngày",
};

export interface Coupon {
  id: string;
  code: string;
  name: string;
  description: string | null;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountVnd: number | null;
  minInvoiceVnd: number;
  validFrom: string;
  validTo: string | null;
  maxRedemptions: number | null;
  maxRedemptionsPerPatient: number | null;
  newPatientRule: NewPatientRule | null;
  newPatientWithinDays: number | null;
  active: boolean;
  /**
   * Enabled and inside its date window right now. Does NOT account for redemptions already used —
   * the backend is explicit that it cannot answer that from the catalogue alone, so the UI must not
   * present this as "usable".
   */
  withinWindow: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CouponForm {
  code: string;
  name: string;
  description?: string | null;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountVnd?: number | null;
  minInvoiceVnd?: number | null;
  validFrom: string;
  validTo?: string | null;
  maxRedemptions?: number | null;
  maxRedemptionsPerPatient?: number | null;
  newPatientRule?: NewPatientRule | null;
  newPatientWithinDays?: number | null;
  active?: boolean;
}

/** The coupon currently applied to an invoice. */
export interface AppliedCoupon {
  redemptionId: string;
  couponId: string;
  code: string;
  name: string;
  discountVnd: number;
}

export const couponQueryKeys = {
  all: ["coupons"] as const,
  list: (activeOnly: boolean) => ["coupons", "list", activeOnly] as const,
  onInvoice: (invoiceId: string) => ["coupons", "invoice", invoiceId] as const,
};

export const couponsApi = {
  list: (activeOnly = false) =>
    apiClient
      .get<Coupon[]>("/v1/coupons", { params: { activeOnly } })
      .then((r) => r.data),

  create: (form: CouponForm) =>
    apiClient.post<{ id: string }>("/v1/coupons", form).then((r) => r.data),

  update: (id: string, form: CouponForm) =>
    apiClient.put<void>(`/v1/coupons/${id}`, form).then((r) => r.data),

  /** Deactivate — the backend never hard-deletes, so past campaigns stay reportable. */
  deactivate: (id: string) => apiClient.delete<void>(`/v1/coupons/${id}`).then((r) => r.data),

  /** What a coupon would take off an invoice of this size, without redeeming it. */
  quote: (id: string, subtotalVnd: number) =>
    apiClient
      .get<{ couponId: string; subtotalVnd: number; discountVnd: number }>(
        `/v1/coupons/${id}/quote`,
        { params: { subtotalVnd } }
      )
      .then((r) => r.data),

  /**
   * The coupon on an invoice. The API answers 204 when there is none, which axios surfaces as an
   * empty body — normalised to null so callers do not have to know that.
   */
  onInvoice: (invoiceId: string) =>
    apiClient
      .get<AppliedCoupon | "">(`/v1/invoices/${invoiceId}/coupon`)
      .then((r) => (r.data ? (r.data as AppliedCoupon) : null)),

  apply: (invoiceId: string, code: string) =>
    apiClient
      .post<AppliedCoupon>(`/v1/invoices/${invoiceId}/coupon`, { code })
      .then((r) => r.data),

  remove: (invoiceId: string) =>
    apiClient.delete<void>(`/v1/invoices/${invoiceId}/coupon`).then((r) => r.data),
};
